import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { FirmSummaryRead } from 'api/models/FirmSummaryRead';
import { FirmsService } from 'api/services/FirmsService';
import {
  FIRM_SUMMARY_DEFAULT_ERROR_MESSAGE,
  FIRM_SUMMARY_QUERY_KEY,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import { GetFirmSummaryReturn, UseGetFirmSummaryParams } from './types';

export type { FirmSummaryRead };

const getFirmSummary = async (
  firmId: number,
  measurementDate?: string,
  fundId?: number,
  breakevenMultiple?: number
): Promise<FirmSummaryRead | null> => {
  try {
    const result = await FirmsService.apiFirmsSummaryRead(
      String(firmId),
      measurementDate || undefined,
      fundId,
      isNil(breakevenMultiple) ? undefined : toString(breakevenMultiple)
    );

    if (!result) {
      return throwError(FIRM_SUMMARY_DEFAULT_ERROR_MESSAGE);
    }

    return result;
  } catch (error) {
    return throwError(FIRM_SUMMARY_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFirmSummary = (params: UseGetFirmSummaryParams): GetFirmSummaryReturn => {
  const { firmId, measurementDate, fundId, breakevenMultiple, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    GetFirmSummaryReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && shouldQueryAutomatically,
    queryFn: () => getFirmSummary(firmId!, measurementDate, fundId, breakevenMultiple),
    queryKey: [FIRM_SUMMARY_QUERY_KEY, firmId, measurementDate, fundId, breakevenMultiple],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
    // Keep showing the previous total Gross IRR while a new measurement date
    // fetches, matching the definition-data grid's keepPreviousData behavior.
    placeholderData: keepPreviousData,
  });

  return {
    data,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFirmSummary;
